import { useState } from 'react';
import moment from 'moment';
import { yupToFormErrors } from 'formik';
import * as Yup from 'yup';

import { useOrderStore } from 'store/orderStore';
import { useCampaignStore } from 'store/campaignStore';
import { BidMode } from 'core/rtbCampaign/singlePage/keyword';
import {
  SelectPattern,
  RunningPattern,
  BudgetType,
  ActionType,
} from 'core/rtbCampaign/singlePage/common';

interface initialKeywords {
  defaultForm: any;
  newForm: any;
  diffDays: number;
}

const maxDayRange = 92;
const forbiddenKeywords = [
  'PChome',
  '網家',
  '蝦皮',
  'shopee',
  'Coupang',
  '酷澎',
];

const useOutboundKeywordValidation = (
  initialState: initialKeywords = {
    defaultForm: {},
    newForm: {},
    diffDays: 0,
  },
) => {
  const { defaultForm, newForm, diffDays } = initialState;
  const { order } = useOrderStore();

  const [errors, setErrors] = useState<any>({ basic: {}, products: '' });
  const { actionType, roasList } = useCampaignStore();

  const outboundBudgetMinimum = 200;
  const customRoasMAx = () => {
    const baseValue = roasList[1];
    if (baseValue === '-') return 50;
    const computed =
      Math.round((Number(baseValue) + Number.EPSILON) * 1.5 * 100) / 100;
    return computed > 50 ? 50 : computed;
  };

  const validationSchema = Yup.object().shape({
    basic: Yup.object().shape({
      advertiserId: Yup.number().required('這裡還是空白的哦！'),
      orderId: Yup.number().required('這裡還是空白的哦！'),
      name: Yup.string().required('這裡還是空白的哦！'),
      startDate: Yup.date().when('runningPattern', {
        is: (runningPattern: string) =>
          runningPattern === RunningPattern.LIMITED,
        then: (schema) =>
          schema.test('min-date', '開始時間不能早於當下時間', (startDate) => {
            const alreadyRunning =
              actionType === ActionType.EDIT &&
              new Date(defaultForm.basic.startDate) <= new Date();

            if (alreadyRunning) {
              return true;
            }

            // 判斷是否為今天
            if (
              moment(moment(startDate).startOf('day')).isSame(
                moment().startOf('day'),
              )
            ) {
              return true;
            }
            return (
              moment(startDate).isSame(moment()) ||
              moment(startDate).isAfter(moment())
            );
          }),
      }),
      endDate: Yup.date().when('runningPattern', {
        is: (runningPattern: string) =>
          runningPattern === RunningPattern.LIMITED,
        then: (schema) =>
          schema
            .test(
              'max-date',
              `請選擇不超出${maxDayRange}天的時間區間`,
              (endDate, { parent }) =>
                moment(endDate).diff(parent.startDate, 'seconds') <=
                maxDayRange * 24 * 60 * 60,
            )
            .test('max-date', '無法選擇過去時間為結束時間', (endDate) =>
              moment(endDate).isAfter(moment()),
            ),
      }),
      selectPattern: Yup.string().required('這裡還是空白的哦！'),
      budgetType: Yup.string().required('這裡還是空白的哦！'),
      roasOption: Yup.object().shape({
        roasStrategy: Yup.string()
          .nullable()
          .when('bidMode', {
            is: (bidMode: string) => bidMode === BidMode.AUTO,
            then: (schema) => schema.required('這裡還是空白的哦！'),
          }),
        roas: Yup.number()
          .nullable()
          .transform((value) => (Number.isNaN(value) ? null : value))
          .when('roasStrategy', {
            is: (roasStrategy: string) => roasStrategy === 'CUSTOM_ROAS',
            then: (schema) =>
              schema
                .required('這裡還是空白的哦！')
                .test('min', '自訂投入產出比需大於 1', (value) => {
                  const isValid = value !== undefined && value !== null;
                  if (isValid) {
                    const isValidValue = value >= 1;
                    return isValidValue;
                  }
                  return true;
                })
                .test(
                  'max',
                  `自訂投入產出比不可高於 ${customRoasMAx()}`,
                  (value) => {
                    const isValid = value !== undefined && value !== null;

                    if (isValid) {
                      const isValidValue = value <= customRoasMAx();
                      return isValidValue;
                    }
                    return true;
                  },
                )
                .test(
                  'is-toFixed-2',
                  `投入產出比僅支援小數點後兩位`,
                  (value) => {
                    const regex = /^-?\d+(\.\d{0,2})?$/;
                    if (value === null) {
                      return true;
                    } else {
                      return (
                        value !== undefined && regex.test(value.toString())
                      );
                    }
                  },
                ),
          }),
      }),
      budget: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .test('is-toFixed-2', `預算僅支援小數點後兩位`, (value) => {
          const regex = /^-?\d+(\.\d{0,2})?$/;
          if (value === null) {
            return true;
          } else {
            return value !== undefined && regex.test(value.toString());
          }
        })
        .when(['runningPattern', 'budgetType'], {
          is: (runningPattern, budgetType) =>
            runningPattern === RunningPattern.LIMITED &&
            budgetType === BudgetType.DAILY,
          then: (schema) =>
            schema
              .required('這裡還是空白的哦！')
              .test(
                'is-positive',
                `已超過總共可支配預算：${order.currency} ${order.budgetBalance}`,
                (value) => {
                  const totalBudget = order.budgetBalance;
                  const remainBudget = totalBudget - (value ?? 0);
                  switch (actionType) {
                    case ActionType.CREATE:
                    case ActionType.COPY:
                      if (remainBudget < 0) {
                        return false;
                      }
                      return true;
                    case ActionType.EDIT:
                      const remainBudgetEdit =
                        totalBudget - (value ?? 0) + defaultForm.basic.budget;
                      if (value === defaultForm.basic.budget) {
                        return true;
                      }
                      if (remainBudgetEdit < 0) {
                        return false;
                      }
                      return true;
                    default:
                      return (
                        value !== undefined &&
                        value !== null &&
                        value <= order.budgetBalance
                      );
                  }
                },
              )
              .test(
                'is-positive',
                `不可低於預期花費：${order.currency} ${defaultForm.basic.expectedSpent}`,
                (value) =>
                  value !== undefined &&
                  value !== null &&
                  value >= defaultForm.basic.expectedSpent,
              ),
        })
        .when('budgetType', {
          is: (val: string) =>
            val === BudgetType.TOTAL && RunningPattern.LIMITED,
          then: (schema) =>
            schema
              .required('這裡還是空白的哦！')
              .test(
                'is-positive',
                `請設定至少總預算 NTD ${outboundBudgetMinimum * diffDays} ，使得建立聯播網廣告`,
                (value) => {
                  switch (actionType) {
                    case ActionType.CREATE:
                    case ActionType.COPY:
                      return (
                        value !== undefined &&
                        value !== null &&
                        value >= outboundBudgetMinimum * diffDays
                      );
                    case ActionType.EDIT:
                      return true;
                    default:
                      return (
                        value !== undefined &&
                        value !== null &&
                        value >= outboundBudgetMinimum * diffDays
                      );
                  }
                },
              )
              .test(
                'is-positive',
                `已超過總共可支配預算：${order.currency} ${order.budgetBalance}`,
                (value) => {
                  const totalBudget = order.budgetBalance;
                  const remainBudget = totalBudget - (value ?? 0);
                  switch (actionType) {
                    case ActionType.CREATE:
                    case ActionType.COPY:
                      if (remainBudget < 0) {
                        return false;
                      }
                      return true;
                    case ActionType.EDIT:
                      const remainBudgetEdit =
                        totalBudget - (value ?? 0) + defaultForm.basic.budget;
                      if (value === defaultForm.basic.budget) {
                        return true;
                      }
                      if (remainBudgetEdit < 0) {
                        return false;
                      }
                      return true;
                    default:
                      return value !== undefined && value !== null
                        ? value <= order.budgetBalance
                        : true;
                  }
                },
              )
              .test(
                'is-positive',
                `不可低於預期花費：${order.currency} ${defaultForm.basic.expectedSpent}`,
                (value) => value >= defaultForm.basic.expectedSpent,
              ),
        }),

      outboundInfo: Yup.object().shape({
        brandNo: Yup.string().test(
          'brand-no-required-if-default',
          '品牌為必填欄位',
          function (value) {
            // Access defaultForm from closure or context
            if (defaultForm.basic?.outboundInfo?.brandNo?.length > 0) {
              return true;
            }
            // Otherwise, skip required validation.
            return Boolean(value && value.length > 0);
          },
        ),
        adContent: Yup.string()
          .required('廣告內容為必填欄位')
          .max(80, '廣告內容不能超過 80 個字元')
          .test(
            'no-links',
            '廣告內容不能包含可連結網址',
            (value) => !/(www|http:\/\/|https:\/\/)/.test(value),
          )
          .test(
            'no-forbidden-keywords',
            '廣告內容不能包含禁用的關鍵字',
            (value) =>
              !forbiddenKeywords.some((keyword) =>
                new RegExp(keyword, 'i').test(value),
              ),
          ),
      }),
      dailyTargetBudget: Yup.number()
        .transform((value) => (Number.isNaN(value) ? null : value))
        .test('is-toFixed-2', `預算僅支援小數點後兩位`, (value) => {
          const regex = /^-?\d+(\.\d{0,2})?$/;
          if (value === null) {
            return true;
          } else {
            return value !== undefined && regex.test(value.toString());
          }
        })
        .when(['runningPattern', 'budgetType'], {
          is: (runningPattern, budgetType) =>
            runningPattern === RunningPattern.LIMITED &&
            budgetType === BudgetType.DAILY,
          then: (schema) =>
            schema
              .required('這裡還是空白的哦！')
              .test(
                'is-positive',
                `請設定至少日預算 NTD ${outboundBudgetMinimum}，使得建立聯播網廣告`,
                (value) => {
                  switch (actionType) {
                    case ActionType.CREATE:
                    case ActionType.COPY:
                      return (
                        value !== undefined && value >= outboundBudgetMinimum
                      );
                    case ActionType.EDIT:
                      return true;
                    default:
                      return (
                        value !== undefined && value >= outboundBudgetMinimum
                      );
                  }
                },
              )
              .test(
                'min-daily-target-budget',
                `已開啟聯播網，日預算無法調整至 200 以下`,
                (value) => {
                  switch (actionType) {
                    case ActionType.EDIT:
                      return (value ?? 0) >= 200;
                    case ActionType.CREATE:
                    case ActionType.COPY:
                      return true;
                    default:
                      return true;
                  }
                },
              ),
        }),
    }),
    autoProducts: Yup.array().when('basic.selectPattern', {
      is: (selectPattern) => selectPattern === SelectPattern.AUTO,
      then: (schema) =>
        schema.min(2, ' 該品牌下商品不足2品，無法投遞廣告，請選擇其它品牌'),
    }),
    limitations: Yup.object()
      .shape({
        include: Yup.array().of(Yup.object().shape({ value: Yup.array() })),
      })
      .when('basic.selectPattern', (selectPattern, schema) =>
        selectPattern[0] === SelectPattern.AUTO &&
        newForm.basic.bidMode === BidMode.AUTO
          ? Yup.object()
              .shape({
                include: Yup.array().of(
                  Yup.object().shape({ value: Yup.array() }),
                ),
              })
              .test(
                'is-positive',
                '您的智能選品未有推薦關鍵字，請選擇其他品牌',
                (value) =>
                  value?.include?.some(
                    (include) => include.value && include.value.length > 0,
                  ),
              )
          : schema,
      ),
  });

  // 即時驗證表單
  const handleValidation = () => {
    validationSchema
      .validate(newForm, { abortEarly: false })
      .then(function () {
        setErrors({});
      })
      .catch(function (err) {
        // console.log('err', err);
        setErrors(yupToFormErrors(err));
        console.log('errors', yupToFormErrors(err));
      });
  };

  return {
    validationSchema,
    errors,
    setErrors,
    handleValidation,
  };
};

export default useOutboundKeywordValidation;
